import { useCallback, useEffect, useRef, useState } from "react"

import { unpackNotebook, type createReportRequest } from "../utils/reportClient"
import { OVERVIEW_TARGET } from "../utils/reportRevisionTypes"
import type { Notebook, NotebookChatTurn } from "../utils/reportTypes"

export function useReportHighlights() {
  const [highlight, setHighlight] = useState({
    notebookId: "",
    targets: [] as string[],
    sequence: 0,
  })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    },
    [],
  )
  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    setHighlight((current) => ({ ...current, targets: [] }))
  }, [])
  const show = useCallback((before: Notebook, after: Notebook) => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    const globalStyleChanged =
      JSON.stringify(before.presentation) !== JSON.stringify(after.presentation)
    const beforeIds = new Set(before.sections.map((section) => section.id))
    const afterIds = new Set(after.sections.map((section) => section.id))
    const beforeOrder = before.sections
      .filter((section) => afterIds.has(section.id))
      .map((section) => section.id)
    const afterOrder = after.sections
      .filter((section) => beforeIds.has(section.id))
      .map((section) => section.id)
    const targets = after.sections
      .filter((section) => {
        const previous = before.sections.find((item) => item.id === section.id)
        return (
          globalStyleChanged ||
          JSON.stringify(previous) !== JSON.stringify(section) ||
          beforeOrder.indexOf(section.id) !== afterOrder.indexOf(section.id)
        )
      })
      .map((section) => section.id)
    if (
      globalStyleChanged ||
      before.title !== after.title ||
      before.summary !== after.summary
    ) {
      targets.push(OVERVIEW_TARGET)
    }
    if (JSON.stringify(before.caveats) !== JSON.stringify(after.caveats)) {
      targets.push("__caveats__")
    }
    setHighlight((current) => ({
      notebookId: after.id,
      targets,
      sequence: current.sequence + 1,
    }))
    timer.current = setTimeout(() => {
      setHighlight((current) => ({ ...current, targets: [] }))
    }, 6500)
    return before.sections
      .filter(
        (section) => !after.sections.some((item) => item.id === section.id),
      )
      .map((section) => section.title)
  }, [])
  const showTurn = async (
    notebook: Notebook,
    turn: NotebookChatTurn,
    request: ReturnType<typeof createReportRequest>,
    signal: AbortSignal,
  ) => {
    const read = async (revision: number) =>
      unpackNotebook(
        await request<Notebook | { notebook: Notebook }>(
          `/api/notebooks/${encodeURIComponent(notebook.id)}?revision=${revision}`,
          undefined,
          "GET",
          signal,
        ),
      )
    const [before, after] = await Promise.all([
      read(turn.revision_before),
      turn.revision_after === notebook.revision
        ? Promise.resolve(notebook)
        : read(turn.revision_after!),
    ])
    if (signal.aborted) {
      return ""
    }
    const removed = show(before, after)
    return removed.length
      ? `Removed: ${removed.join(", ")}. Other changes are highlighted in the current notebook.`
      : "Changed sections are highlighted in the current notebook."
  }
  return { highlight, show, showTurn, clear }
}
